import {
  Attachments,
  Bubble,
  Conversations,
  Prompts,
  Sender,
  Welcome,
  useXAgent,
  useXChat,
} from "@ant-design/x";

import {
  senderPromptsItems,
  defaultConversationsItems,
  placeholderPromptsItems,
  symptomCategories,
} from "../components/items";

import {
  CloudUploadOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PaperClipOutlined,
  PlusOutlined,
  LoginOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";

import {
  Typography,
  Form,
  Input,
  Select,
  Radio,
  Space,
  Button,
  Drawer,
  message,
} from "antd";

import { useNavigate } from "react-router-dom";
import { Badge, type GetProp } from "antd";
import { useEffect, useState, useRef } from "react";
import { XStream } from "@ant-design/x";
import markdownit from "markdown-it";

import useAppStyles from "../styles/AppStyles";
import ConversationItem from "../components/conversation";
import { useAuth } from "../context/AuthContext";

// 初始化markdown解析器
const md = markdownit({ html: true, breaks: true });

// markdown渲染函数
const renderMarkdown = (content: string) => (
  <Typography>
    <div dangerouslySetInnerHTML={{ __html: md.render(content) }} />
  </Typography>
);

// 修改roles定义，将needtyping的使用移动到AppContent组件内
const roles = (needtyping: boolean): GetProp<typeof Bubble.List, "roles"> => ({
  ai: {
    placement: "start",
    typing: needtyping ? { step: 5, interval: 20 } : false,
    styles: {
      content: {
        borderRadius: 16,
      },
    },
    messageRender: renderMarkdown,
  },
  local: {
    placement: "end",
    variant: "shadow",
  },
});

const UserPage = () => {
  // ==================== Style ====================
  const { styles } = useAppStyles();
  const navigate = useNavigate();
  const { authState } = useAuth();

  // ==================== State ====================
  const [headerOpen, setHeaderOpen] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [content, setContent] = useState("");
  const [conversationsItems, setConversationsItems] = useState<
    ConversationItem[]
  >(defaultConversationsItems);
  const [activeKey, setActiveKey] = useState(defaultConversationsItems[0].key);
  const [attachedFiles, setAttachedFiles] = useState<
    GetProp<typeof Attachments, "items">
  >([]);
  // 使用useState和useRef共同管理conversationId
  const [conversationId, setConversationId] = useState<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  // 添加一个状态来追踪是否收到了第一个数据块
  const [receivedFirstChunk, setReceivedFirstChunk] = useState(false);
  // 添加AbortController引用
  const abortControllerRef = useRef<AbortController | null>(null);

  // 将needtyping的状态定义移到组件内部
  const [needtyping, setNeedtyping] = useState(true);

  // 添加全局变量
  const [gender, setGender] = useState<string>("");
  const [age, setAge] = useState<string>("");
  const [symptom, setSymptom] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  const [userInfoSubmitted, setUserInfoSubmitted] = useState<boolean>(false);

  // 添加ref来保存用户信息
  const genderRef = useRef<string>("");
  const ageRef = useRef<string>("");
  const symptomRef = useRef<string>("");
  const userIdRef = useRef<string>("");

  // 保持ref与state同步
  useEffect(() => {
    conversationIdRef.current = conversationId;
    console.log("conversationId状态已更新:", conversationId);

    // 如果conversationId存在且activeKey有效，更新conversationsItems中对应项的conversationId
    if (conversationId && activeKey) {
      setConversationsItems((prev) =>
        prev.map((item) =>
          item.key === activeKey ? { ...item, conversationId } : item
        )
      );
      console.log(
        `已将conversationId ${conversationId} 绑定到对话 ${activeKey}`
      );
    }
  }, [conversationId]);

  // 初始化用户ID为当前登录用户的username
  useEffect(() => {
    if (authState.user?.username && !userInfoSubmitted) {
      setUserId(authState.user.username);
      userIdRef.current = authState.user.username;
      console.log("已设置用户ID:", authState.user.username);
    }
  }, [authState.user?.username, userInfoSubmitted]);

  // ==================== Runtime ====================
  const [agent] = useXAgent({
    request: async (info, callbacks) => {
      const { messages, message } = info;
      const { onSuccess, onUpdate, onError } = callbacks;

      // current message
      console.log("message", message);

      // history messages
      console.log("messages", messages);

      let fullContent = "";
      try {
        // 在开始请求时重置接收状态
        setReceivedFirstChunk(false);

        // 创建新的AbortController并保存到ref
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        // 使用ref获取最新的conversationId值
        const currentConversationId = conversationIdRef.current;
        console.log("当前conversation_id (从ref获取):", currentConversationId);

        // 构建URL，如果有conversationId则添加为查询参数
        const url = "https://api.ibase.site/v1/workflows/chat";

        console.log("请求URL:", url);

        // 准备请求体
        const requestBody: any = {
          bot_id: "7494815815846789120",
          workflow_id: "7530892409069223975",
          conversation_id: currentConversationId,
          stream: true,
          auto_save_history: true,
          additional_messages: [{ role: "user", content: message }],
          parameters: {
            user_id: userIdRef.current,
            gender: genderRef.current,
            age: ageRef.current,
            symptom: symptomRef.current,
            conversation_id: currentConversationId,
          },
        };
        console.log("gender", genderRef.current);
        console.log("age", ageRef.current);
        console.log("symptom", symptomRef.current);

        console.log("完整请求体:", JSON.stringify(requestBody));

        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          signal, // 添加signal用于中断请求
        });

        if (!res.ok) {
          throw new Error(`API返回错误：${res.status} ${res.statusText}`);
        }

        for await (const chunk of XStream({ readableStream: res.body! })) {
          // 检查是否已被中断
          if (signal.aborted) {
            console.log("请求已被中断");
            break;
          }
          console.log("chunk", chunk);

          // if (chunk.event === "conversation.chat.created") {
          //   console.log("收到chat创建事件:", chunk.data);
          //   try {
          //     const chatData = JSON.parse(chunk.data);
          //     if (chatData && chatData.conversation_id) {
          //       console.log("更新conversation_id:", chatData.conversation_id);
          //       // 更新ref和state
          //       conversationIdRef.current = chatData.conversation_id;
          //       setConversationId(chatData.conversation_id);
          //     }
          //   } catch (e) {
          //     console.error("解析创建事件数据失败:", e);
          //   }
          // }

          // 检查是否有数据字段
          if (chunk.data) {
            // 检查不同的可能格式
            if (chunk.event === " conversation.message.delta") {
              console.log("收到delta数据:", chunk.data);
              try {
                const deltaData = JSON.parse(chunk.data);
                deltaData.content = deltaData.content.replace(
                  "\\n\\n",
                  "\n\n  "
                );
                fullContent += deltaData.content;

                // 标记已收到第一个数据块
                if (!receivedFirstChunk) {
                  setReceivedFirstChunk(true);
                }

                onUpdate(fullContent);
              } catch (e) {
                console.error("解析delta数据失败:", e);
              }
            }
          }
        }

        if (!fullContent) {
          console.warn("未收到任何回复内容，返回默认回复");
          fullContent = "抱歉，我暂时无法回答您的问题。请稍后再试。";
        }

        //console.log("完整回复内容:", fullContent);
        console.log("请求完成后的conversation_id:", conversationIdRef.current);

        // 如果请求被中断，只更新到目前为止的内容，但不标记为成功完成
        onSuccess(fullContent);
      } catch (error) {
        // 如果是AbortError，不将其视为错误
        if ((error as any).name === "AbortError") {
          console.log("请求被用户中断");
          onSuccess(fullContent);
          onError(error as Error);
        } else {
          // 处理其他错误
          console.error("API请求失败:", error);
          onError(error as Error);
        }
      } finally {
        // 清理AbortController
        abortControllerRef.current = null;
      }
    },
  });

  const { onRequest, messages, setMessages } = useXChat({
    agent,
  });

  useEffect(() => {
    if (activeKey !== undefined) {
      // 查找对应的会话项
      const activeConversation = conversationsItems.find(
        (item) => item.key === activeKey
      );

      // 如果找到了会话项且它有conversationId
      if (activeConversation && activeConversation.conversationId) {
        console.log(
          `切换到现有对话 ${activeKey}，关联的conversationId是 ${activeConversation.conversationId}`
        );
        // 更新当前conversationId
        setConversationId(activeConversation.conversationId);
        conversationIdRef.current = activeConversation.conversationId;

        // 获取历史消息
        fetchHistoryMessages(activeConversation.conversationId);
      } else {
        // 如果是新对话或没有conversationId的对话
        setMessages([]);
        setNeedtyping(true);
        // 重置conversationId的state和ref
        setConversationId(null);
        conversationIdRef.current = null;
        console.log("新建对话或切换到无历史的对话，重置conversation_id为null");
      }
    }
  }, [activeKey]);

  // 获取历史消息的函数
  const fetchHistoryMessages = async (conversationId: string) => {
    try {
      console.log(`正在获取对话 ${conversationId} 的历史消息...`);

      const response = await fetch(
        `https://api.ibase.site/v1/conversation/message/list?conversation_id=${conversationId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `获取历史消息失败：${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log("历史消息API返回数据:", data);

      // 定义消息类型
      interface HistoryMessageItem {
        id: string;
        content: string;
        role: string;
        created_at: number;
        [key: string]: any;
      }

      // 确保返回了有效数据
      if (
        data &&
        data.data &&
        Array.isArray(data.data) &&
        data.data.length > 0
      ) {
        // 根据API返回的数据结构处理历史消息
        // 注意：API返回的消息顺序可能是倒序的，需要进行排序
        const historyMessages = data.data
          .sort(
            (a: HistoryMessageItem, b: HistoryMessageItem) =>
              a.created_at - b.created_at
          ) // 按时间顺序排序
          .map((msg: HistoryMessageItem) => ({
            id: msg.id,
            message: msg.content,
            status: msg.role === "user" ? "local" : "done",
          }));

        console.log("处理后的历史消息:", historyMessages);

        // 更新消息列表
        setNeedtyping(false);
        setMessages(historyMessages);
        message.success("已加载历史消息");
      } else {
        console.log("没有找到历史消息或数据格式不正确");
        setMessages([]);
        setNeedtyping(false);
        message.info("没有找到历史消息");
      }
    } catch (error) {
      console.error("获取历史消息失败:", error);
      message.error("获取历史消息失败");
    }
  };

  useEffect(() => {
    // 初始化时创建新的conversationId，而不是重置为null
    const createInitialConversation = async () => {
      try {
        console.log("组件初始化，创建新的conversation_id");
        // 创建新会话并获取conversationId
        const response = await fetch(
          "https://api.ibase.site/v1/conversation/create",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              bot_id: "7494815815846789120",
            }),
          }
        );

        if (!response.ok) {
          throw new Error(
            `创建会话失败: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();
        const newConversationId = data.data?.id;

        console.log("初始化成功创建新会话，conversationId:", newConversationId);

        // 更新conversationId
        setConversationId(newConversationId);
        conversationIdRef.current = newConversationId;

        // 更新默认会话的conversationId
        if (conversationsItems.length > 0 && activeKey) {
          setConversationsItems((prev) =>
            prev.map((item) =>
              item.key === activeKey
                ? { ...item, conversationId: newConversationId }
                : item
            )
          );
        }
      } catch (error) {
        console.error("初始化创建会话失败:", error);
        // 失败时设置为null
        setConversationId(null);
        conversationIdRef.current = null;
      }
    };

    createInitialConversation();
  }, []);

  // ==================== Event ====================
  const onSubmit = (nextContent: string) => {
    if (!nextContent) return;

    // 检查当前会话是否有消息，如果没有则更新会话标题为消息前10个字
    if (messages.length === 0 && activeKey !== undefined) {
      // 截取消息的前10个字作为标题，如果不足10个字则全部使用
      const newLabel =
        nextContent.length > 10
          ? nextContent.substring(0, 10) + "..."
          : nextContent;

      // 更新conversationsItems中当前会话的label
      setConversationsItems((prev) =>
        prev.map((item) =>
          item.key === activeKey ? { ...item, label: newLabel } : item
        )
      );
    }

    onRequest(nextContent);
    setContent("");
    // 在移动端提交后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
  };

  const onPromptsItemClick: GetProp<typeof Prompts, "onItemClick"> = (info) => {
    // 检查当前会话是否有消息，如果没有则更新会话标题为提示词内容的前10个字
    if (messages.length === 0 && activeKey !== undefined) {
      const description = info.data.description as string;
      // 截取提示词内容的前10个字作为标题
      const newLabel =
        description.length > 10
          ? description.substring(0, 10) + "..."
          : description;

      // 更新conversationsItems中当前会话的label
      setConversationsItems((prev) =>
        prev.map((item) =>
          item.key === activeKey ? { ...item, label: newLabel } : item
        )
      );
    }

    onRequest(info.data.description as string);
    // 在移动端点击提示词后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
  };

  const onSenderPromptsItemClick: GetProp<typeof Prompts, "onItemClick"> = (
    info
  ) => {
    let found = false;
    for (let i = 0; i < senderPromptsItems.length; i++) {
      if (
        content.indexOf(`@${senderPromptsItems[i].description as string}, `) !==
        -1
      ) {
        setContent(
          content.replace(
            `@${senderPromptsItems[i].description as string}, `,
            `@${info.data.description as string}, `
          )
        );
        found = true;
        console.log("替换成功");
        break;
      }
    }
    if (!found) {
      setContent(`@${info.data.description as string}, ` + content);
    }
    // 在移动端点击提示词后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
  };

  const onAddConversation = async () => {
    handleCancelRequest();

    try {
      // 创建新会话并获取conversationId
      const response = await fetch(
        "https://api.ibase.site/v1/conversation/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            bot_id: "7494815815846789120",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `创建会话失败: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      const newConversationId = data.data?.id;

      console.log("成功创建新会话，conversationId:", newConversationId);

      // 更新conversationId
      setConversationId(newConversationId);
      conversationIdRef.current = newConversationId;

      // 创建新会话项
      const newKey = `${conversationsItems.length}`;
      setConversationsItems([
        ...conversationsItems,
        {
          key: newKey,
          label: `新问诊对话`,
          conversationId: newConversationId,
        },
      ]);
      setActiveKey(newKey);
      setMessages([]);
      setNeedtyping(true);
    } catch (error) {
      console.error("创建新会话失败:", error);
      message.error("创建新会话失败，请重试");

      // 创建本地会话项，但没有conversationId
      const newKey = `${conversationsItems.length}`;
      setConversationsItems([
        ...conversationsItems,
        {
          key: newKey,
          label: `新问诊对话`,
        },
      ]);
      setActiveKey(newKey);
      setMessages([]);
      setNeedtyping(true);
      setConversationId(null);
      conversationIdRef.current = null;
    }

    // 在移动端添加会话后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
  };

  const onConversationClick: GetProp<typeof Conversations, "onActiveChange"> = (
    key
  ) => {
    handleCancelRequest();
    setActiveKey(key);
    // 在移动端选择会话后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
  };

  const handleFileChange: GetProp<typeof Attachments, "onChange"> = (info) =>
    setAttachedFiles(info.fileList);

  // 处理登录按钮点击
  const handleLoginClick = () => {
    navigate("/login");
  };

  // 添加中断请求的函数
  const handleCancelRequest = () => {
    if (abortControllerRef.current) {
      console.log("用户取消请求");
      abortControllerRef.current.abort();
    }
  };

  // 添加表单提交函数
  const handleUserInfoSubmit = () => {
    if (!gender || !age || !symptom || !userId) {
      message.error("请填写完整信息");
      return;
    }
    setUserInfoSubmitted(true);
    message.success("信息已提交");

    // 更新ref的值
    genderRef.current = gender;
    ageRef.current = age;
    symptomRef.current = symptom;
    userIdRef.current = userId;

    // 可以在这里使用这些变量或者发送到服务器
    console.log("用户信息:", { gender, age, symptom, userId });

    // 自动发送"我要预问诊"消息
    onRequest("我要预问诊");
  };

  // ==================== Nodes ====================
  const placeholderNode = (
    <Space direction="vertical" size={16} className={styles.placeholder}>
      <Welcome
        variant="borderless"
        icon="https://dk.ibase.site/dakanglogo.png"
        title="你好，我是达康AI医疗辅助系统"
        description="达康AI医疗辅助系统（试运行版），一个专注于预问诊的智能助手，并为您提供专业的健康咨询建议。"
        extra={
          <Space>
            <Button icon={<LoginOutlined />} onClick={handleLoginClick} />
          </Space>
        }
      />

      <div className={styles.userForm}>
        <div className={styles.formTitle}>请填写基本信息</div>
        <Form layout="vertical" style={{ maxWidth: 500, margin: "0 auto" }}>
          <Form.Item label="用户ID/手机号" required>
            <Input
              value={userId}
              readOnly
              placeholder="当前登录用户"
              style={{ backgroundColor: '#f5f5f5' }}
            />
          </Form.Item>

          <Form.Item label="性别" required>
            <Radio.Group
              value={gender}
              onChange={(e) => setGender(e.target.value)}
            >
              <Radio value="男">男</Radio>
              <Radio value="女">女</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item label="年龄" required>
            <Input
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder="请输入年龄"
            />
          </Form.Item>

          <Form.Item label="症状" required>
            <Select
              options={symptomCategories.flatMap(category => category.children)}
              placeholder="请选择症状"
              value={symptom || undefined}
              onChange={(value: string) => {
                setSymptom(value || '');
              }}
              showSearch
              filterOption={(input: string, option: any) => {
                return option?.label && typeof option.label === 'string' && 
                  option.label.toLowerCase().includes(input.toLowerCase());
              }}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" block onClick={handleUserInfoSubmit}>
              我要预问诊
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Space>
  );

  const items: GetProp<typeof Bubble.List, "items"> = [
    ...messages.map(({ id, message, status }) => ({
      key: id,
      role: status === "local" ? "local" : "ai",
      content: message,
    })),
    // 只有在agent请求中且尚未收到第一个数据块时才显示loading状态
    ...(agent.isRequesting() && !receivedFirstChunk
      ? [
          {
            key: "loading-message",
            role: "ai",
            content: "",
            loading: true,
          },
        ]
      : []),
  ];

  // console.log("当前消息列表:", messages);
  // console.log("气泡项:", items);
  // console.log("是否收到第一个数据块:", receivedFirstChunk);

  // 在组件内部使用roles函数
  const currentRoles = roles(needtyping);

  const attachmentsNode = (
    <Badge dot={attachedFiles.length > 0 && !headerOpen}>
      <Button
        type="text"
        icon={<PaperClipOutlined />}
        onClick={() => setHeaderOpen(!headerOpen)}
      />
    </Badge>
  );

  const senderHeader = (
    <Sender.Header
      title="Attachments"
      open={headerOpen}
      onOpenChange={setHeaderOpen}
      styles={{
        content: {
          padding: 0,
        },
      }}
    >
      <Attachments
        beforeUpload={() => false}
        items={attachedFiles}
        onChange={handleFileChange}
        placeholder={(type) =>
          type === "drop"
            ? { title: "Drop file here" }
            : {
                icon: <CloudUploadOutlined />,
                title: "上传文件",
                description: "点击或拖拽文件到此区域上传",
              }
        }
      />
    </Sender.Header>
  );

  const logoNode = (
    <div className={styles.logo}>
      <img src="./dakanglogo.png" draggable={false} alt="logo" />
      <span>达康AI医疗辅助系统</span>
    </div>
  );

  const mobileLogoNode = (
    <div className={styles.mobileLogo}>
      <img src="./dakanglogo.png" draggable={false} alt="logo" />
      <span>达康AI医疗辅助系统</span>
    </div>
  );

  const menuContent = (
    <>
      {logoNode}
      <Button
        onClick={() => navigate("/")}
        type="link"
        className={styles.addBtn}
        icon={<ArrowLeftOutlined />}
      >
        返回主页
      </Button>
      <Button
        onClick={onAddConversation}
        type="link"
        className={styles.addBtn}
        icon={<PlusOutlined />}
      >
        新问诊对话
      </Button>
      <Conversations
        items={conversationsItems}
        className={styles.conversations}
        activeKey={activeKey}
        onActiveChange={onConversationClick}
      />
    </>
  );

  // ==================== Render =================
  return (
    <div className={styles.layout}>
      {/* 移动端顶部导航 */}
      <div className={styles.mobileHeader}>
        {mobileLogoNode}
        <Space>
          <Button
            type="text"
            icon={<LoginOutlined />}
            onClick={handleLoginClick}
          />
          <Button
            type="text"
            icon={drawerVisible ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
            onClick={() => setDrawerVisible(!drawerVisible)}
          />
        </Space>
      </div>

      {/* 移动端抽屉菜单 */}
      <Drawer
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        placement="left"
        width={280}
        className={styles.drawerMenu}
        title="达康AI医疗辅助系统（试运行版）"
        footer={null}
      >
        {menuContent}
      </Drawer>

      {/* 桌面端左侧菜单 */}
      <div className={styles.menu}>{menuContent}</div>

      <div className={styles.chat}>
        {/* 🌟 消息列表 */}
        <Bubble.List
          items={
            items.length > 0
              ? items
              : [{ content: placeholderNode, variant: "borderless" }]
          }
          roles={currentRoles}
          className={styles.messages}
        />
        {/* 🌟 只在用户信息提交后显示输入相关组件 */}
        {userInfoSubmitted && (
          <>
            {/* 🌟 输入框 */}
            <Sender
              value={content}
              header={senderHeader}
              onSubmit={onSubmit}
              onChange={setContent}
              loading={agent.isRequesting()}
              className={styles.sender}
              onCancel={handleCancelRequest} // 添加取消回调
            />
          </>
        )}
      </div>
    </div>
  );
};

export default UserPage;
